import React, { useEffect, useMemo, useState } from "react";
import onboardingData from "../data/private/onboarding.json";
import { getProgress, logActivity } from "../utils/activity";
import InfoBanner from "./InfoBanner";
import ProgressTracker from "./ProgressTracker";
import { usePlatformContext } from "../context/PlatformContext";

const STORAGE_KEY = "myv_onboarding_state";

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { stepIndex: 0, dismissed: false };
  } catch (error) {
    return { stepIndex: 0, dismissed: false };
  }
};

const writeState = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    // no-op
  }
};

export default function OnboardingTour() {
  const { session } = usePlatformContext();
  const progress = getProgress();
  const storedState = readState();
  const initialIndex = useMemo(() => {
    if (storedState.dismissed) return -1;
    if (Number.isInteger(storedState.stepIndex)) {
      return storedState.stepIndex;
    }
    const firstIncomplete = onboardingData.steps.findIndex(
      (step) => !progress[step.key]
    );
    return firstIncomplete === -1 ? onboardingData.steps.length : firstIncomplete;
  }, [progress, storedState.dismissed, storedState.stepIndex]);

  const [stepIndex, setStepIndex] = useState(initialIndex);

  useEffect(() => {
    if (stepIndex >= onboardingData.steps.length) {
      writeState({ stepIndex, dismissed: true, completedAt: Date.now() });
    } else if (stepIndex >= 0) {
      writeState({ stepIndex, dismissed: false });
    }
  }, [stepIndex]);

  if (session?.profileCompleted) {
    return null;
  }

  if (stepIndex < 0 || stepIndex >= onboardingData.steps.length) {
    return null;
  }

  const step = onboardingData.steps[stepIndex];

  const handleNext = () => {
    logActivity("onboarding_step_completed", {
      key: step.key,
      label: step.title,
      index: stepIndex,
    });
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
  };

  const handleBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleDismiss = () => {
    writeState({ stepIndex, dismissed: true, dismissedAt: Date.now() });
    setStepIndex(-1);
  };

  return (
    <div className="card p-6">
      <p className="text-sm font-semibold text-slate-900">
        {onboardingData.title}
      </p>
      <p className="mt-1 text-sm text-slate-600">{onboardingData.subtitle}</p>
      <div className="mt-4">
        <ProgressTracker
          steps={onboardingData.steps.map((item) => ({
            key: item.key,
            label: item.shortLabel || item.title,
          }))}
          completedMap={progress}
          activeIndex={stepIndex}
        />
      </div>
      <div className="mt-4">
        <InfoBanner title={step.title} description={step.description} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {stepIndex > 0 && (
          <button className="btn btn-secondary" onClick={handleBack}>
            {onboardingData.actions.backLabel}
          </button>
        )}
        <button className="btn btn-primary" onClick={handleNext}>
          {stepIndex === onboardingData.steps.length - 1
            ? onboardingData.actions.completeLabel
            : onboardingData.actions.nextLabel}
        </button>
        <button className="btn btn-secondary" onClick={handleDismiss}>
          {onboardingData.actions.dismissLabel}
        </button>
      </div>
    </div>
  );
}
